"use client";

import { EnterpriseHero } from "@/components/EnterpriseHero";
import { EnterpriseFeatures } from "@/components/EnterpriseFeatures";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import {
  Wrench,
  Bolt,
  Hammer,
  Snowflake,
  TreeDeciduous,
  Paintbrush,
  Home,
  Sparkles,
  ChevronRight,
  ArrowRight,
  PhoneCall,
  ArrowUpRight,
  ClipboardList,
  Users,
  MessageCircle,
  CreditCard
} from "lucide-react";

// Real logos pulled from contractors already on the platform (Contractor.logo_url).
// Curated for clean/white backgrounds, and excludes Inner West Sydney suburbs
// (client's current marketing focus - real Inner West signups will replace these).
const TRUSTED_CONTRACTORS = [
  { name: "Emerald Bridge Pty Ltd", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/3980001_3985000/3982254/thumbnail.gif" },
  { name: "Air Technician Group", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4030001_4035000/4032335/thumbnail.gif" },
  { name: "Trueform Constructions Pty Ltd", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/3970001_3975000/3973926/thumbnail.gif" },
  { name: "Vista Tiling", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/3120001_3125000/3124520/thumbnail.gif" },
  { name: "A.M.A Electrical Group", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4060001_4065000/4060552/thumbnail.gif" },
  { name: "True Blue Plumbing Services", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4275001_4280000/4276083/thumbnail.gif" },
  { name: "Vertex Property Care", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4195001_4200000/4195375/thumbnail.gif" },
  { name: "JWC Plumbing Services", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/3810001_3815000/3813333/thumbnail.gif" },
  { name: "Woodpecker Carpenters Pty Ltd", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4070001_4075000/4070880/thumbnail.gif" },
  { name: "Fox Mowing & Gardening", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/1065001_1070000/1067254/thumbnail.gif" },
  { name: "Nanak Interiors", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4000001_4005000/4001009/thumbnail.gif" },
  { name: "AMW Plumbing Group", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4125001_4130000/4128045/thumbnail.gif" },
  { name: "DC Windows & Doors", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4065001_4070000/4065926/thumbnail.gif" },
  { name: "DN Earthworks", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4045001_4050000/4048190/thumbnail.gif" },
  { name: "Edge Builds Pty Ltd", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4160001_4165000/4160021/thumbnail.gif" },
  { name: "Hydroforce Landscaping", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4155001_4160000/4157993/thumbnail.gif" },
  { name: "Howden Roofing", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/3720001_3725000/3721507/thumbnail.gif" },
  { name: "Linear Concepts", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4125001_4130000/4129229/thumbnail.gif" },
  { name: "Loel Homes", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/3765001_3770000/3769523/thumbnail.gif" },
  { name: "Supa-Fresh Kleene", logo: "https://mediacache.homeimprovementpages.com.au/creative/sites/4195001_4200000/4199017/thumbnail.gif" },
];

const SERVICES = [
  { id: "plumbing", icon: <Wrench size={20} />, label: "Plumbing", color: "text-blue-500", bg: "bg-blue-50" },
  { id: "electrical", icon: <Bolt size={20} />, label: "Electrical", color: "text-amber-500", bg: "bg-amber-50" },
  { id: "handyman", icon: <Hammer size={20} />, label: "Handyman", color: "text-orange-500", bg: "bg-orange-50" },
  { id: "hvac", icon: <Snowflake size={20} />, label: "HVAC", color: "text-sky-500", bg: "bg-sky-50" },
  { id: "painting", icon: <Paintbrush size={20} />, label: "Painting", color: "text-rose-500", bg: "bg-rose-50" },
  { id: "roofing", icon: <Home size={20} />, label: "Roofing", color: "text-slate-500", bg: "bg-slate-50" },
  { id: "landscaping", icon: <TreeDeciduous size={20} />, label: "Landscaping", color: "text-emerald-500", bg: "bg-emerald-50" },
  { id: "cleaning", icon: <Sparkles size={20} />, label: "Cleaning", color: "text-violet-500", bg: "bg-violet-50" },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Describe the job",
    body: "Tell us what's wrong, add a photo if you can, and get an instant AI-estimated price range before anyone's involved.",
    icon: <ClipboardList size={22} />,
    color: "emerald"
  },
  {
    step: "02",
    title: "Get competing bids",
    body: "Verified local pros in your area bid with their own price and availability. You're never stuck with just one quote.",
    icon: <Users size={22} />,
    color: "blue"
  },
  {
    step: "03",
    title: "Choose, chat & track",
    body: "Compare bids, pick your contractor, and coordinate through in-app chat or a masked call. Your number stays private.",
    icon: <MessageCircle size={22} />,
    color: "purple"
  },
  {
    step: "04",
    title: "Pay after it's done",
    body: "No payment upfront. Confirm the work is complete, approve any added parts, then pay securely and leave a review.",
    icon: <CreditCard size={22} />,
    color: "amber"
  },
];

const HOW_IT_WORKS_COLORS: Record<string, { bg: string; text: string; ring: string }> = {
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600", ring: "hover:ring-emerald-50" },
  blue: { bg: "bg-blue-50", text: "text-blue-600", ring: "hover:ring-blue-50" },
  purple: { bg: "bg-purple-50", text: "text-purple-600", ring: "hover:ring-purple-50" },
  amber: { bg: "bg-amber-50", text: "text-amber-600", ring: "hover:ring-amber-50" },
};

export default function HomePage() {
  return (
    <main className="min-h-screen bg-white overflow-hidden">

      {/* Hero */}
      <EnterpriseHero />

      {/* Trust Bar */}
      <section className="py-12 border-y border-slate-100 bg-slate-50/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-8">Trusted by property leaders</p>
        </div>
        <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
          <div className="marquee-track flex w-max flex-nowrap items-center gap-10 md:gap-14 grayscale opacity-50">
            {[...TRUSTED_CONTRACTORS, ...TRUSTED_CONTRACTORS].map((c, i) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img key={`${c.name}-${i}`} src={c.logo} alt={c.name} title={c.name} className="h-9 md:h-10 w-auto object-contain shrink-0" />
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <EnterpriseFeatures />

      {/* Services Grid */}
      <section className="py-24 bg-slate-50" id="services">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-emerald-600 font-bold tracking-widest uppercase text-xs mb-3">Capabilities</p>
              <h2 className="text-3xl md:text-5xl font-bold text-slate-950 tracking-tight">Expertise in every trade.</h2>
            </div>
            <Link href="/booking" className="inline-flex items-center gap-2 text-slate-600 font-bold hover:text-emerald-600 transition-colors">
              Request custom service
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => (
              <Link
                key={i}
                href={`/booking?category=${encodeURIComponent(s.id)}`}
                className="group p-6 rounded-3xl bg-white border border-slate-200 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-900/5 transition-all"
              >
                <div className={`w-12 h-12 rounded-2xl ${s.bg} ${s.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  {s.icon}
                </div>
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900">{s.label}</h3>
                  <ArrowUpRight size={16} className="text-slate-300 group-hover:text-emerald-500 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="text-emerald-600 font-bold tracking-widest uppercase text-xs mb-3">The Process</p>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-950 tracking-tight mb-4">From problem to paid, in 4 steps.</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">No fixed quote, no upfront payment — real local pros bid for your job and you stay in control the whole way.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {/* Connecting Line (desktop only) */}
            <div className="hidden lg:block absolute top-[28px] left-[12.5%] right-[12.5%] h-[1px] bg-slate-100 -z-0" />

            {HOW_IT_WORKS.map((step, i) => {
              const colors = HOW_IT_WORKS_COLORS[step.color];
              return (
                <div key={i} className="relative z-10 flex flex-col items-start p-6 rounded-3xl border border-slate-100 hover:border-slate-200 hover:shadow-xl hover:shadow-slate-900/5 transition-all bg-white">
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-14 h-14 rounded-2xl ${colors.bg} ${colors.text} flex items-center justify-center ring-8 ring-transparent ${colors.ring} transition-all`}>
                      {step.icon}
                    </div>
                    <span className="text-3xl font-black text-slate-100">{step.step}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-950 mb-2">{step.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{step.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative overflow-hidden">
        {/* Background Accents */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-900/20 rounded-full blur-[120px] -mr-[250px] -mt-[250px]" />

        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-8">
            Ready to experience <br />the <span className="text-emerald-400 font-medium">modern standard?</span>
          </h2>
          <p className="text-slate-400 text-lg mb-12 max-w-xl mx-auto">
            Join the forward-thinking enterprises and homeowners who manage their property with FIXER.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/register"
              className="w-full sm:w-auto px-10 py-4 rounded-2xl bg-emerald-600 text-white font-bold hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-900/20 active:scale-95"
            >
              Start Free Today
            </Link>
            <Link
              href="/contractor/onboarding"
              className="w-full sm:w-auto px-10 py-4 rounded-2xl bg-white/10 text-white font-bold hover:bg-white/20 transition-all backdrop-blur-sm border border-white/10"
            >
              For Contractors
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
